import { Urls } from '@/lib/urls';
import { get } from '@/requests/api/get';
import type { ChatMessage } from '@pigello/pigello-chat';
import type {
  ModelName,
  OrganizationUser,
  Tenant,
} from '@pigello/pigello-matrix';
import { useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { toast, type ExternalToast } from 'sonner';

export function useOnMessage() {
  const pathname = usePathname();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [lastMessage, setLastMessage] = useState<ChatMessage | null>(null);

  const onMessage = useCallback(
    async (message: ChatMessage) => {
      const toastOptions: ExternalToast = {
        position: 'top-right',
        description: () => {
          if (message.message.length > 100) {
            return message.message.slice(0, 100) + '...';
          }
          return message.message;
        },
        action: {
          label: 'Visa',
          onClick: () => {
            router.push(Urls.dashboard.inbox.chat(message.chat_group_key));
          },
        },
      };

      const senderId = message.created_by_id;
      const modelName = message.created_by_type.split('.').at(1) as
        | ModelName
        | undefined;

      if (!modelName) {
        toast.info('Nytt meddelande', toastOptions);
        return;
      }

      try {
        const user = await queryClient.ensureQueryData<
          Tenant | OrganizationUser
        >({
          queryKey: [modelName, senderId],
          queryFn: () => get({ modelName, id: senderId }),
        });

        if (!user) {
          toast.info('Nytt meddelande', toastOptions);
          return;
        }

        const userTypeLabel = modelName === 'organizationuser' ? 'Org' : 'HG';
        toast.message(
          `Nytt meddelande från ${user.communicationName} (${userTypeLabel})`,
          toastOptions
        );
      } catch (error) {
        console.warn('Failed to fetch user data for chat message:', error);
        toast.error('Nytt meddelande', toastOptions);
      }
      setLastMessage(null);
    },
    [queryClient, router]
  );

  useEffect(() => {
    if (!lastMessage) {
      return;
    }
    if (pathname.includes('/chat')) {
      setLastMessage(null);
      return;
    }

    onMessage(lastMessage);
  }, [lastMessage, onMessage, pathname]);

  return { setLastMessage };
}
